import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { createElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { Editor } from '@tiptap/react';
import { CodeBlockToolbar, CODE_BLOCK_LANGUAGES } from './code-block-toolbar';
import { codeBlockPositions, CODE_BLOCK_ACTIONS, isMermaidBlock } from './code-block-actions';
import { toggleMermaidDiagram } from './mermaid-view-store';

/**
 * Per-code-block toolbar, rendered as a ProseMirror **widget decoration** — one
 * toolbar anchored to each code block, positioned by the editor itself
 * (absolute, top-right inside the `position: relative` <pre>). Shown on hover
 * via pure CSS (`.nula-editor-root pre:hover .code-block-toolbar`).
 *
 * Why a widget decoration and NOT a React node view: CodeBlockShiki highlights
 * the block's content via ProseMirror decorations and dispatches
 * force-decoration transactions. Wrapping that content in a `NodeViewContent`
 * React node view made Tiptap's node-view renderer call `flushSync` mid-render
 * — a reentrancy render storm that froze the editor. A widget decoration is a
 * sibling overlay the editor manages; it never touches the content DOM, so
 * Shiki keeps sole ownership. React is mounted into the widget's own container
 * via `createRoot` (async/scheduled — no synchronous flushSync reentrancy).
 */
export const CodeBlockToolbarExtension = Extension.create({
  name: 'codeBlockToolbar',

  addProseMirrorPlugins() {
    const editor = this.editor as Editor;

    const buildDecorations = (doc: typeof editor.state.doc) =>
      DecorationSet.create(
        doc,
        codeBlockPositions(doc).map(({ pos, language }) =>
          Decoration.widget(
            // +1 places the widget just inside the code block, so it renders as
            // a child of the <pre> (which is position: relative) — letting the
            // absolutely-positioned toolbar pin to the block's top-right corner.
            pos + 1,
            () => {
              const container = document.createElement('div');
              container.className = 'code-block-toolbar';
              container.contentEditable = 'false';
              const root: Root = createRoot(container);
              root.render(
                createElement(CodeBlockToolbar, {
                  language,
                  languages: CODE_BLOCK_LANGUAGES,
                  getText: () => editor.state.doc.nodeAt(pos)?.textContent ?? '',
                  onCopy: () => CODE_BLOCK_ACTIONS.copy(editor, pos),
                  onDelete: () => CODE_BLOCK_ACTIONS.remove(editor, pos),
                  onLanguageChange: (lang: string | null) =>
                    CODE_BLOCK_ACTIONS.setLanguage(editor, lang, pos),
                  // Mermaid blocks (explicit language OR mermaid-looking
                  // content) get a diagram/code toggle that flips this pos in
                  // the view store.
                  onToggleView: isMermaidBlock(language, doc.nodeAt(pos)?.textContent ?? '')
                    ? () => toggleMermaidDiagram(pos)
                    : undefined,
                }),
              );
              // Unmount the React root asynchronously when ProseMirror removes
              // the widget (can't unmount synchronously during render).
              (container as HTMLElement & { __destroy?: () => void }).__destroy = () =>
                queueMicrotask(() => root.unmount());
              return container;
            },
            {
              // Stable key per block position so ProseMirror reuses the same
              // widget DOM across transactions instead of remounting React.
              key: `code-block-toolbar-${pos}`,
              side: -1,
              destroy: (node) =>
                (node as HTMLElement & { __destroy?: () => void }).__destroy?.(),
            },
          ),
        ),
      );

    return [
      new Plugin({
        key: new PluginKey('codeBlockToolbar'),
        state: {
          init: (_, { doc }) => buildDecorations(doc),
          apply(tr, old) {
            // Only rebuild when the document changed; otherwise map the existing
            // set forward (cheap) so we don't remount toolbars on every
            // selection/decoration transaction.
            return tr.docChanged ? buildDecorations(tr.doc) : old.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});
